// src/utils/serviceWorkerRegistration.js
//
// Registers the Workbox service worker built by vite-plugin-pwa (dist/sw.js).
//
// The page is never reloaded by the service worker on its own. That used to
// happen in two ways:
//   1. First visit: the new worker claimed the page once its precache finished
//      (seconds to minutes in), `controllerchange` fired and the page reloaded
//      mid-signup / mid-exam.
//   2. Every deploy: the 30 s update poll found the new worker, which activated
//      straight away and reloaded every open tab, exams included.
//
// Now a new version is downloaded in the background and waits. It is applied
// only when the user navigates to a page where a full load loses nothing
// (see applyUpdateOnNavigation), or when every tab of the site has been closed.

const UPDATE_CHECK_INTERVAL = 15 * 60 * 1000;
// A navigation this soon after a click/tap/key press counts as user-initiated.
// Redirects that run by themselves after landing (auth, exam-slug, onboarding
// guards) must not trigger the update, or it would look like a reload on load.
const USER_NAVIGATION_WINDOW = 2000;

// Pages where a reload would cost the user something: an exam in progress,
// in-memory results, sign-in / payment / onboarding flows.
const PROTECTED_PATH = /^\/[a-z]{2}\/(?:auth|verified|checkout|onboarding|admin|try|dev)(?:\/|$)|\/(?:simulator|paper|results|instructions|wizard)(?:\/|$)/;

let registration = null;
let applying = false;
let lastInteractionAt = 0;

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;

  if (import.meta.env.DEV) {
    // A worker left over from a production preview would serve stale bundles.
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => registrations.forEach((r) => r.unregister()))
      .catch(() => {});
    return;
  }

  const markInteraction = () => { lastInteractionAt = Date.now(); };
  window.addEventListener('pointerdown', markInteraction, { capture: true, passive: true });
  window.addEventListener('keydown', markInteraction, { capture: true, passive: true });

  const register = () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((r) => {
        registration = r;
        const check = () => r.update().catch(() => {});
        setInterval(check, UPDATE_CHECK_INTERVAL);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
        window.addEventListener('online', check);
      })
      .catch((error) => console.log('SW registration failed:', error));
  };

  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
};

export const isUpdateWaiting = () => Boolean(registration?.waiting && navigator.serviceWorker.controller);

/**
 * Called after an in-app navigation. If a new version is waiting, the user just
 * clicked or typed, and the page they moved to is safe to reload, activate the
 * new worker and load the page again. It reads as the page they chose opening.
 */
export const applyUpdateOnNavigation = (pathname) => {
  if (applying || !isUpdateWaiting() || PROTECTED_PATH.test(pathname)) return false;
  if (Date.now() - lastInteractionAt > USER_NAVIGATION_WINDOW) return false;

  applying = true;
  const waiting = registration.waiting;
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
  waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
};
